import { useMemo } from 'react';
import { useFormContext, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiMagnifyingGlass as SearchIcon } from 'react-icons/hi2';
import { Input, Label, RichTextRenderer } from '@knack/asterisk-react';

import {
  type SearchView,
  type SearchViewFilters,
  type SearchViewInput
} from '@/types/schema/views/SearchView';
import { sanitizeHTML } from '@/utils/sanitizeHTML';
import { cn } from '@/utils/tailwind';
import { FormErrorMessage } from '@/components/errors/FormErrorMessage';
import { useViewContext } from '@/components/views/ViewContext';
import { SearchInstructions } from './SearchInstructions';

type KeywordSearchInputProps = {
  input: SearchViewInput;
  filterRuleIndex: number;
  errors: FieldErrors<SearchViewFilters>;
};

export function KeywordSearchInput({ input, filterRuleIndex, errors }: KeywordSearchInputProps) {
  const [t] = useTranslation();

  const { view } = useViewContext<SearchView>();
  const { register } = useFormContext<SearchViewFilters>();

  const hasError = errors.rules && errors.rules[filterRuleIndex];
  const sanitizedName = useMemo(
    () => sanitizeHTML(input.name || t('actions.search')),
    [input.name, t]
  );

  return (
    <div
      className={cn('flex gap-2', {
        'flex-col': view.label_format === 'top',
        'items-center': view.label_format === 'left' || view.label_format === 'right'
      })}
    >
      <Label
        className={cn('font-medium', {
          'text-destructive': hasError,
          'text-right': view.label_format === 'right',
          'text-left': view.label_format === 'left',
          'w-1/4 min-w-24 max-w-52 truncate lg:min-w-32':
            view.label_format === 'right' || view.label_format === 'left'
        })}
        isRequired={input.required}
      >
        <RichTextRenderer dangerouslySetInnerHTML={{ __html: sanitizedName }} />
      </Label>

      <div className="flex-1">
        <Input.Container>
          <Input
            data-testid="keyword-search-input"
            className="h-9"
            intent={hasError ? 'destructive' : 'default'}
            {...register(`rules.${filterRuleIndex}.value`)}
          />
          <Input.Icon>
            <SearchIcon size={14} className="shrink-0" />
          </Input.Icon>
        </Input.Container>

        {errors.rules && (
          <FormErrorMessage
            errors={errors}
            name={`rules.${filterRuleIndex}.value`}
            className="mt-1"
          />
        )}
        {input.instructions && <SearchInstructions>{input.instructions}</SearchInstructions>}
      </div>
    </div>
  );
}
